import {
  ApolloCache,
  BaseMutationOptions,
  MutationHookOptions,
  MutationUpdaterFunction,
  OperationVariables,
  gql,
  useMutation,
} from "@apollo/client";
import { getAuctionRequestEventQuery } from "apollo/hooks/queries";
import {
  ADD_ACCOUNT_ROLES,
  ADD_CAREPROVIDER_ACCOUNT_ROLES,
  ADD_CAREPROVIDER_MEMBER,
  ADD_CAREPROVIDER_MEMBER_TOKEN,
  ADD_PRIORITY_PROVIDER,
  ARCHIVE_PATIENT,
  ARCHIVE_PROVIDER_SEARCH_PATIENT,
  BATCH_ADD_PROVIDER_ACCOUNTS,
  CAREPROVIDER_ACCOUNT_ASSOCIATION,
  CHECK_SEALD_SESSIONS,
  COMPLETE_PRODUCT_TOUR,
  CONNEXT_AUTHENTICATION,
  CREATE_ACCOUNT,
  CREATE_ACCOUNTS_BATCH,
  CREATE_ACCOUNT_FOR_CARESEEKER,
  CREATE_ADDITIONAL_AUCTION,
  CREATE_ADMISSIONS_RESOURCES,
  CREATE_AUCTION,
  CREATE_CAREPROVIDER,
  CREATE_CAREPROVIDER_REVERSE_SEARCH,
  CREATE_CARESEEKER,
  CREATE_CHAT,
  CREATE_COMPANY,
  CREATE_CONSULTANT,
  CREATE_EMAIL_TRANSLATION,
  CREATE_ENCRYPTION_CONTEXT,
  CREATE_FILE,
  CREATE_INSURANCE,
  CREATE_NATIVE_TRANSLATION,
  CREATE_NATIVE_TRANSLATIONS,
  CREATE_ONPREMISE_CONFIG,
  CREATE_ONPREMISE_IMPORT,
  CREATE_OR_UPDATE_CAREPROVIDER_PRODUCT_FEATURE,
  CREATE_OR_UPDATE_CARESEEKER_PRODUCT_FEATURE,
  CREATE_PATIENT,
  CREATE_PENDING_SESSION_KEY,
  CREATE_PROVIDERSEARCH_CARESEEKER,
  CREATE_PROVIDER_SEARCH_ACCOUNT,
  CREATE_SSO_INTEGRATION,
  CREATE_TRANSLATION,
  CREATE_TRANSLATIONS,
  CREATE_USER_MANAGEMENT_FILE,
  DELETE_ACCOUNT_ROLE,
  DELETE_BEDROCK_EXAMPLE,
  DELETE_BEDROCK_PROMPT,
  DELETE_BEDROCK_RESPONSE,
  DELETE_BEDROCK_SCHEMA,
  DELETE_CAREPROVIDER_ACCOUNT_ROLE,
  DELETE_CAREPROVIDER_DPA,
  DELETE_CAREPROVIDER_FILE,
  DELETE_CAREPROVIDER_REVERSE_SEARCH,
  DELETE_CHAT,
  DELETE_CONSULTANT_FILE,
  DELETE_EMAIL_TRANSLATION,
  DELETE_FILE,
  DELETE_NATIVE_TRANSLATION,
  DELETE_PRIORITY_PROVIDER,
  DELETE_SEALD_ACCESS,
  DELETE_SSO_INTEGRATION,
  DELETE_TRANSLATION,
  DELETE_USER_MANAGEMENT_FILE,
  FEEDBACK_PATIENT_INFO_MISSING,
  FORGOT_PASSWORD,
  GET_ACCOUNTS,
  GET_ACCOUNT_WITHIN_CARESEEKER_GROUP,
  GET_ADDRESS,
  GET_AUCTIONREQUEST,
  GET_CAREPROVIDER,
  GET_CAREPROVIDER_FILES,
  GET_CAREPROVIDER_REVERSE_SEARCHES,
  GET_CARESEEKER,
  GET_CARESEEKER_ACCOUNTS,
  GET_COMPANY,
  GET_CONSULTANT_FILES,
  GET_DOCTOR_REHAB_FORMS,
  GET_FILES,
  GET_PATIENT,
  GET_RECEIVER_ACCOUNTS,
  GET_SENDER_RECEIVER_NOTE,
  INITIATE_SEALD_IDENTITY,
  LOGIN_PROVIDER_SEARCH_ACCOUNT,
  NOTIFY_RECEIVER_RESPONSIBLE_PERSON,
  NOTIFY_SOCIAL_WORKER,
  ONPREMISE_DTA_FILE_NUMBER,
  POST_ENCRYPTION_JOB,
  POST_RECEIVER_DOCS_API_FILE,
  REQUEST_BULK_ACTION,
  SAVE_BEDROCK_EXAMPLE,
  SAVE_BEDROCK_PROMPT,
  SAVE_BEDROCK_RESPONSE,
  SAVE_BEDROCK_SCHEMAS,
  SEALD_GROUP_RESET,
  SEND_ADMISSIONS_LOGIN_EMAIL,
  SEND_FAX_FOR_AUCTION_REQUEST,
  SEND_ONPREMISE_LOGS,
  SEND_TEST_EMAIL,
  START_ELECTIVE_FLOW,
  UPDATE_ACCOUNT,
  UPDATE_ACCOUNT_FOR_CARESEEKER,
  UPDATE_ACCOUNT_ROLE_STATION_MAPPING,
  UPDATE_ADMISSIONS_AUCTION,
  UPDATE_ADMISSIONS_RESOURCES,
  UPDATE_ASSOCIATIONS,
  UPDATE_BROWSER_SUBSCRIPTION,
  UPDATE_CAPACITY,
  UPDATE_CAREPROVIDER,
  UPDATE_CAREPROVIDER_ACCOUNT,
  UPDATE_CAREPROVIDER_ACCOUNT_TOKEN,
  UPDATE_CAREPROVIDER_REVERSE_SEARCH,
  UPDATE_CAREPROVIDER_TOKEN,
  UPDATE_CARESEEKER,
  UPDATE_CHAT,
  UPDATE_COMPANY,
  UPDATE_COMPANY_TOKEN,
  UPDATE_CONSULTANT,
  UPDATE_DOCTOR_REHAB_FORMS,
  UPDATE_EMAIL_TRANSLATION,
  UPDATE_FILE,
  UPDATE_IK_NUMBERS,
  UPDATE_INSURANCE,
  UPDATE_LOOKER_EMBED_TOKEN,
  UPDATE_NATIVE_TRANSLATION,
  UPDATE_ONPREMISE_CONFIG,
  UPDATE_RECEIVER_SESSION_KEYS,
  UPDATE_REQUEST_SESSION_KEYS,
  UPDATE_SEARCH_CANDIDATES,
  UPDATE_SENDER_RECEIVER_NOTE,
  UPDATE_SSO_INTEGRATION,
  UPDATE_TRANSLATION,
  UPLOAD_CAREPROVIDER_DPA,
  UPLOAD_CAREPROVIDER_FILE,
  UPLOAD_CONSULTANT_FILE,
  UPSERT_ADMISSION_FORM,
  USE_REQUEST_ACTION,
  USE_SEARCH_ACTION,
} from "core/apollo/graphql";
import { careproviderDpa } from "core/apollo/graphql/fragments/fragments";
import {
  DOCUMENT_DATA_PROCESSING_AGREEMENT,
  DOCUMENT_TERMS_AND_CONDITIONS,
  FILE_TYPES,
  REDIRECT_PARAM,
  REPOSITORY_VOICE,
  RETURN_PARAM,
} from "core/consts";
import { getCreateAccountErrorMessage } from "core/model/accounts/accountErrorHandling";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getReferrer } from "core/model/utils/urls";
import {
  Account,
  AccountRoleStationInput,
  AddressCircle,
  AdmissionsForm,
  AnyObject,
  AppType,
  Auction,
  AuctionRequest,
  BedrockExample,
  BedrockPrompt,
  BedrockResponse,
  BedrockResponsePayload,
  BedrockSchema,
  BrowserSubscription,
  Candidates,
  Capacity,
  Careprovider,
  CareproviderDpaFile,
  CareproviderFile,
  Careseeker,
  Chat,
  Company,
  Consultant,
  Country,
  CreateUserManagmentFileParams,
  DPAFileType,
  EmbedCookielessSessionAcquireResponse,
  EncryptedField,
  EncryptionContext,
  EncryptionJobRequest,
  FaxResponse,
  FileCategory,
  FileParams,
  FileScope,
  FileShareMode,
  Filev2,
  Insurance,
  OauthAuthenticationInput,
  OnPremImport,
  OnpremiseConfig,
  OnpremiseConfigInput,
  Patient,
  ProductFeature,
  ProductFeatureRequestPayload,
  ProductTourCompletion,
  ProviderBlackListing,
  RecareFileType,
  RehabForm,
  RequestActionType,
  SSOIntegration,
  SealdAccessPayload,
  SealdIdentityInput,
  SealdIdentityPayload,
  SealdSessionsPayload,
  SearchActionsWithHandlers,
  SenderReceiverNote,
  SenderUserManagementFile,
  SessionKey,
  UndoActionType,
  ValueOf,
} from "core/types";
import { getUnixTime } from "date-fns";
import { useAuctionRequestEventsContext } from "dsl/atoms/AuctionRequestEventsContext";
import {
  useLegalDocuments,
  useLegalDocumentsContext,
} from "dsl/atoms/LegalDocuments";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { SendEmail } from "dsl/ecosystems/AcpTestEmail";
import {
  EmailRepositoryType,
  EmailTranslation,
  FormValue,
  RepositoryType,
  TranslationType,
  TranslationTypes,
} from "dsl/ecosystems/AcpTranslations/types";
import {
  ReceiverFilters,
  ReverseSearch,
} from "dsl/organisms/Filters/ReceiverApp/types";
import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { useTranslations } from "translations";
import {
  useGenericMutation,
  useGenericProgress,
  useRefetchOnConflict,
} from "./utils";

type UpdateDoctorRehabFormsData = { formData?: RehabForm };
type UpdateDoctorRehabFormsVariables = {
  auctionId: number | undefined;
  careseekerId: number;
  patientId: number | undefined;
};
export type CreateChatData = {
  chat: Pick<Chat, "id">;
};

export type CreateChatVariables = {
  input?: Partial<Chat> & { account_id?: number };
};

export type UpdateChatData = {
  chat: Chat;
};

export type UpdateChatVariables = {
  id?: number;
  input?: Partial<Chat>;
  account_id?: number;
};

export type DeleteChatData = {
  id: number;
};

export type DeleteChatVariables = {
  id: number;
};

export function useUpdateRequestSessionKeys({
  auctionRequestId,
  onCompleted,
  onError,
}: {
  auctionRequestId: number | undefined;
  onCompleted?: (data: Readonly<any>) => void;
  onError?: (error: any) => void;
}) {
  const [mutation, progress, reset, data, error] = useGenericMutation<
    any,
    { auctionRequestId: number; input: AnyObject }
  >(UPDATE_REQUEST_SESSION_KEYS, {
    onCompleted,
    onError,
    variables: { auctionRequestId: auctionRequestId ?? -1 },
  });

  return [mutation, progress, reset, data, error] as const;
}

export function useCreatePendingSessionKey({
  accountId,
  auctionRequestId,
  careproviderId,
  careseekerId,
  onCompleted,
  onError,
}: {
  accountId: number | undefined;
  auctionRequestId: number | undefined;
  careproviderId: number | undefined;
  careseekerId: number | undefined;
  onCompleted?: (data: Readonly<any>) => void;
  onError?: (error: any) => void;
}) {
  const [mutation, progress, reset, data, error] = useGenericMutation<
    any,
    {
      input: {
        account_id: number | undefined;
        auction_request_id: number | undefined;
        careprovider_id: number | undefined;
        careseeker_id: number | undefined;
      };
    }
  >(CREATE_PENDING_SESSION_KEY, {
    onCompleted,
    onError,
    variables: {
      input: {
        account_id: accountId,
        auction_request_id: auctionRequestId,
        careprovider_id: careproviderId,
        careseeker_id: careseekerId,
      },
    },
  });

  return [mutation, progress, reset, data, error] as const;
}

export function useUpdateTranslation(
  repo: RepositoryType,
  options?: MutationHookOptions<
    { translation: TranslationType },
    { id: string; input: TranslationType }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { translation: TranslationType },
    { id: string; input: TranslationType }
  >(
    repo === REPOSITORY_VOICE ? UPDATE_NATIVE_TRANSLATION : UPDATE_TRANSLATION,
    options,
  );
  const [updateTranslation, progress, reset] = useGenericProgress<
    { translation: TranslationType },
    { id: string; input: TranslationType }
  >(mutation, { error, loading });

  const translation = data?.translation;
  return [updateTranslation, progress, reset, translation, error] as const;
}

export function useCreateTranslation(
  repo: RepositoryType,
  options?: MutationHookOptions<
    { translation: TranslationType },
    { input?: Partial<TranslationType> }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { translation: TranslationType },
    { input?: Partial<TranslationType> }
  >(
    repo === REPOSITORY_VOICE ? CREATE_NATIVE_TRANSLATION : CREATE_TRANSLATION,
    options,
  );
  const [updateTranslation, mutationProgress, resetProgress] =
    useGenericProgress<
      { translation: TranslationType },
      { input?: Partial<TranslationType> }
    >(mutation, {
      error,
      loading,
    });

  const mutate = useCallback(
    (input?: Partial<TranslationType> | undefined) =>
      updateTranslation({ variables: { input } }),
    [updateTranslation],
  );

  const translation = data?.translation;
  return [mutate, mutationProgress, resetProgress, translation, error] as const;
}

export function useDeleteTranslation(
  repo: RepositoryType,
  options?: MutationHookOptions<
    { translation: TranslationType },
    { id: string }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { translation: TranslationType },
    { id: string }
  >(
    repo === REPOSITORY_VOICE ? DELETE_NATIVE_TRANSLATION : DELETE_TRANSLATION,
    options,
  );
  const [updateTranslation, progress, reset] = useGenericProgress<
    { translation: TranslationType },
    { id: string }
  >(mutation, { error, loading });

  const translation = data?.translation;
  return [updateTranslation, progress, reset, translation, error] as const;
}

export function usePostTranslations(
  repo: RepositoryType | EmailRepositoryType,
  options?: MutationHookOptions<
    { translations: TranslationTypes },
    { input: Partial<TranslationType>[] }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { translations: TranslationTypes },
    { input: Partial<TranslationType>[] }
  >(
    repo === REPOSITORY_VOICE
      ? CREATE_NATIVE_TRANSLATIONS
      : CREATE_TRANSLATIONS,
    options,
  );
  const [updateTranslations, progress, reset] = useGenericProgress<
    { translations: TranslationTypes },
    { input: Partial<TranslationType>[] }
  >(mutation, { error, loading });

  const translation = data?.translations;
  return [updateTranslations, progress, reset, translation, error] as const;
}

export function useRequestBulkAction() {
  const [mutation, { data, error, loading }] = useMutation<
    { requestBulkAction: AuctionRequest },
    {
      account_id: number;
      action_type: string;
      careprovider_id: number;
      input: { id: number }[] | undefined;
    }
  >(REQUEST_BULK_ACTION, {
    refetchQueries: ["careproviderRequestsNew"],
  });
  const [requestBulkAction, progress, reset] = useGenericProgress<
    { requestBulkAction: AuctionRequest },
    {
      account_id: number;
      action_type: string;
      careprovider_id: number;
      input: { id: number }[] | undefined;
    }
  >(mutation, { error, loading });

  return [requestBulkAction, progress, reset, data, error] as const;
}

export function useAddPriorityProvider({
  careseekerId,
  onCompleted,
}: {
  careseekerId: number;
} & MutationHookOptions<
  { notifySocialWorker: unknown },
  { careseekerId: number; input: number }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { notifySocialWorker: unknown },
      { careseekerId: number; input: number }
    >(ADD_PRIORITY_PROVIDER, {
      onCompleted,
      refetchQueries: ["getCareseeker"],
      variables: { careseekerId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeletePriorityProvider({
  careproviderId,
  careseekerId,
  onCompleted,
}: {
  careproviderId: number;
  careseekerId: number;
} & MutationHookOptions<
  { notifySocialWorker: unknown },
  {
    careproviderId: number;
    careseekerId: number;
    input: Partial<Careprovider>;
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { notifySocialWorker: unknown },
      {
        careproviderId: number;
        careseekerId: number;
        input: Partial<Careprovider>;
      }
    >(DELETE_PRIORITY_PROVIDER, {
      onCompleted,
      variables: { careproviderId, careseekerId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useNotifySocialWorker({
  onCompleted,
  patientId,
}: {
  patientId: number;
} & MutationHookOptions<
  { notifySocialWorker: unknown },
  { id: number; input: { message: string } }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { notifySocialWorker: unknown },
      { id: number; input: { message: string } }
    >(NOTIFY_SOCIAL_WORKER, {
      onCompleted,
      variables: { id: patientId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useNotifyReceiverAssignee({
  auctionRequestId,
  onCompleted,
}: {
  auctionRequestId: number;
} & MutationHookOptions<
  { notifySocialWorker: unknown },
  { id: number; input: { message: string } }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { notifySocialWorker: unknown },
      { id: number; input: { message: string } }
    >(NOTIFY_RECEIVER_RESPONSIBLE_PERSON, {
      onCompleted,
      variables: { id: auctionRequestId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export type RejectedProvider = {
  careprovider: Careprovider;
  solution: number;
};

export function useSendFaxForAuctionRequest() {
  const [mutate, { data, error, loading }] = useMutation<
    { fax: FaxResponse },
    { auctionRequestId: number; input: { content: string; fax_type: number } }
  >(SEND_FAX_FOR_AUCTION_REQUEST);
  const [sendFax, mutationProgress, resetProgress] = useGenericProgress<
    { fax: FaxResponse },
    { auctionRequestId: number; input: { content: string; fax_type: number } }
  >(mutate, { error, loading });

  return [sendFax, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAuction(props?: {
  patient?: Patient;
  onUpdate?: (proxy: ApolloCache<any>, auction: Auction | undefined) => void;
}) {
  const encryptionContext = useApolloEncryptionContext({
    decrypt: true,
  });

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ auction: Auction }, { input: Partial<Auction> }>(
      CREATE_AUCTION,
      {
        context: { encryptionContext },
        update: (proxy, newAuctionData) => {
          const { auction: newAuction } = newAuctionData?.data || {};

          props?.onUpdate?.(proxy, newAuction);

          if (!props?.patient || !newAuction) return;

          const patientWithNewAuction = {
            ...props.patient,
            auctions: [...(props.patient.auctions || []), newAuction],
          };

          proxy.writeQuery({
            data: {
              patient: patientWithNewAuction,
            },
            query: GET_PATIENT,
            variables: { id: props.patient.id },
          });
        },
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateCompany({
  id,
  onCompleted,
  onError,
  token,
}: {
  id: number;
  token?: string;
} & MutationHookOptions<
  { company: Company },
  {
    id: number;
    input: Pick<Company, "ik_numbers" | "parent_company_id">;
    token?: string;
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { company: Company },
      {
        id: number;
        input: Pick<Company, "ik_numbers" | "parent_company_id">;
        token?: string;
      }
    >(token ? UPDATE_COMPANY_TOKEN : UPDATE_COMPANY, {
      onCompleted,
      onError,
      variables: { id, token },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateAccount({
  id,
  onCompleted,
}: {
  id?: number;
} & MutationHookOptions<
  { account: Account },
  { id?: number; input: Partial<Account>; jwt?: string }
> = {}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { account: Account },
      { id?: number; input: Partial<Account>; jwt?: string }
    >(UPDATE_ACCOUNT, {
      onCompleted,
      variables: { id },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateSSOIntegration({
  onCompleted,
  onError,
}: MutationHookOptions<{ createSSOIntegration: SSOIntegration }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { createSSOIntegration: SSOIntegration },
      { input: Partial<SSOIntegration> }
    >(CREATE_SSO_INTEGRATION, {
      onCompleted,
      onError,
      refetchQueries: ["getCareseeker"],
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateSSOIntegration({
  onCompleted,
  onError,
}: {
  id: number | undefined;
} & MutationHookOptions<{ sso_integration: SSOIntegration }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { sso_integration: SSOIntegration },
      { input: Partial<SSOIntegration> }
    >(UPDATE_SSO_INTEGRATION, {
      onCompleted,
      onError,
      refetchQueries: ["getCareseeker"],
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeleteSSOIntegration({
  onCompleted,
  onError,
}: MutationHookOptions<{ ssoIntegration: SSOIntegration }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ ssoIntegration: SSOIntegration }, { id: number }>(
      DELETE_SSO_INTEGRATION,
      {
        onCompleted,
        onError,
        refetchQueries: ["getCareseeker"],
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateCareproviderAccount({
  accountId,
  careproviderId,
  onCompleted,
  token,
}: {
  accountId?: number;
  careproviderId: number;
  token?: string;
} & MutationHookOptions<
  { careproviderAccount: Account },
  {
    accountId?: number;
    careproviderId: number;
    input?: Partial<Account>;
    token?: string;
  }
>) {
  const [apolloMutation, { data, error, loading }] = useMutation<
    { careproviderAccount: Account },
    {
      accountId?: number;
      careproviderId: number;
      input?: Partial<Account>;
      token?: string;
    }
  >(token ? UPDATE_CAREPROVIDER_ACCOUNT_TOKEN : UPDATE_CAREPROVIDER_ACCOUNT, {
    onCompleted,
    variables: { accountId, careproviderId, token },
  });
  const [updateAccount, mutationProgress, resetProgress] = useGenericProgress<
    { careproviderAccount: Account },
    {
      accountId?: number;
      careproviderId: number;
      input?: Partial<Account>;
      token?: string;
    }
  >(apolloMutation, { error, loading });

  return [updateAccount, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateReceiverSessionKey(
  patientId: number,
  receiverId: number,
) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { sessionKeys: unknown },
      {
        id: number;
        input: {
          account_id?: number;
          patient_id: number;
          session_key?: string;
        }[];
      }
    >(UPDATE_RECEIVER_SESSION_KEYS, {
      refetchQueries: [
        {
          query: GET_PATIENT,
          variables: { id: patientId },
        },
      ],
      variables: { id: receiverId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateReceiversSessionKeys(
  options?: MutationHookOptions<
    { sessionKeys: SessionKey[] },
    {
      id: number | undefined;
      input: {
        account_id?: number;
        patient_id: number;
        session_key?: string;
      }[];
    }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { sessionKeys: SessionKey[] },
    {
      id: number | undefined;
      input: {
        account_id?: number;
        patient_id: number;
        session_key?: string;
      }[];
    }
  >(UPDATE_RECEIVER_SESSION_KEYS, options);

  const [updateSessionKeys, progress, reset] = useGenericProgress<
    { sessionKeys: SessionKey[] },
    {
      id: number | undefined;
      input: {
        account_id?: number;
        patient_id: number;
        session_key?: string;
      }[];
    }
  >(mutation, { error, loading });

  const sessionKeys = data?.sessionKeys;
  return [updateSessionKeys, progress, reset, sessionKeys, error] as const;
}

export function useUpdateInsurance({
  insuranceId,
  onCompleted,
}: {
  insuranceId: number;
} & MutationHookOptions<
  { insurance: Insurance },
  { id: number; input: { message: string } }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { insurance: Insurance },
      { id: number; input: { message: string } }
    >(UPDATE_INSURANCE, {
      onCompleted,
      variables: { id: insuranceId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useAddAccountRoles({
  careproviderId,
  careseekerId,
  ...options
}: MutationHookOptions<{ accountRole: Account }> & {
  careproviderId?: number;
  careseekerId?: number;
} = {}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { accountRole: Account },
      {
        input: {
          account_id: number;
          careprovider_id?: number;
          careseeker_id?: number;
          role_value: number;
        }[];
      }
    >(ADD_ACCOUNT_ROLES, {
      update: (cache, { data }) => {
        const queryData = (() => {
          if (careseekerId)
            return {
              query: GET_CARESEEKER_ACCOUNTS,
              variables: { id: careseekerId },
            };
          if (careproviderId)
            return {
              query: GET_RECEIVER_ACCOUNTS,
              variables: { id: careproviderId },
            };

          return {
            query: GET_ACCOUNTS,
            variables: {},
          };
        })();

        const response = cache.readQuery<{ accounts: Account[] }>({
          query: queryData.query,
          variables: queryData.variables,
        });

        const accounts = response?.accounts;
        const newAccount = data?.accountRole;
        const updatedAccounts = accounts?.map((account) =>
          newAccount?.id === account.id
            ? { ...account, roles: { ...account.roles, ...newAccount.roles } }
            : account,
        );

        if (newAccount && accounts) {
          cache.writeQuery({
            data: {
              accounts: updatedAccounts,
            },
            query: queryData.query,
            variables: queryData.variables,
          });
        }
      },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeleteAccountRoles(
  options: MutationHookOptions<
    {
      accountRole: {
        accountId: number;
        queryVars: string;
        role_value: number;
      };
    },
    { accountId: number; queryVars: string; role_value: number }
  > = {},
) {
  const [apolloMutation, { data, error, loading }] = useMutation<
    {
      accountRole: {
        accountId: number;
        queryVars: string;
        role_value: number;
      };
    },
    { accountId: number; queryVars: string; role_value: number }
  >(DELETE_ACCOUNT_ROLE, { ...options });

  const [deleteRole, mutationProgress, resetProgress] = useGenericProgress<
    {
      accountRole: {
        accountId: number;
        queryVars: string;
        role_value: number;
      };
    },
    { accountId: number; queryVars: string; role_value: number }
  >(apolloMutation, { error, loading });

  return [deleteRole, mutationProgress, resetProgress, data, error] as const;
}

export function useAddCareproviderAccountRoles({
  careproviderId,
  onCompleted,
}: {
  careproviderId: number;
} & MutationHookOptions<
  { careproviderAccountRole: unknown },
  {
    careproviderId: number;
    input: {
      account_id: number;
      careprovider_id: number;
      role_value: number;
    }[];
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careproviderAccountRole: unknown },
      {
        careproviderId: number;
        input: {
          account_id: number;
          careprovider_id: number;
          role_value: number;
        }[];
      }
    >(ADD_CAREPROVIDER_ACCOUNT_ROLES, {
      onCompleted,
      variables: { careproviderId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeleteCareproviderAccountRole({
  accountId,
  careproviderId,
  onCompleted,
  queryVars,
  role_value,
}: {
  accountId: number | undefined;
  careproviderId: number;
  queryVars: string;
  role_value: number;
} & MutationHookOptions<
  { careproviderAccountRole: unknown },
  {
    accountId: number | undefined;
    careproviderId: number;
    queryVars: string;
    role_value: number;
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careproviderAccountRole: unknown },
      {
        accountId: number | undefined;
        careproviderId: number;
        queryVars: string;
        role_value: number;
      }
    >(DELETE_CAREPROVIDER_ACCOUNT_ROLE, {
      onCompleted,
      variables: { accountId, careproviderId, queryVars, role_value },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateInsurance({
  onCompleted,
}: MutationHookOptions<
  { insurance: Insurance },
  { input: { message: string } }
> = {}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { insurance: Insurance },
      { input: { message: string } }
    >(CREATE_INSURANCE, {
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateCareseeker({
  onCompleted,
}: MutationHookOptions<
  { careseeker: Careseeker },
  { input: Partial<Careseeker> }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careseeker: Careseeker },
      { input: Partial<Careseeker> }
    >(CREATE_CARESEEKER, {
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

const updateCompanyCareproviders: MutationUpdaterFunction<
  { careprovider: Careprovider },
  OperationVariables,
  any,
  ApolloCache<any>
> = (proxy, { data }) => {
  const careprovider = data?.careprovider;
  const companyId = careprovider?.company?.id;
  if (companyId) {
    const getCompany = proxy.readQuery<{ company: Company }>({
      query: GET_COMPANY,
      variables: { id: companyId },
    });

    const company = getCompany?.company;
    if (company)
      proxy.writeQuery<{ company: Company }>({
        data: {
          company: {
            ...company,
            careproviders: [...(company?.careproviders ?? []), careprovider],
          },
        },
        query: GET_COMPANY,
        variables: { id: companyId },
      });
  }
};

export function useCreateCareprovider({
  onCompleted,
}: MutationHookOptions<{ careprovider: Careprovider }> = {}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careprovider: Careprovider },
      { input: Partial<Careprovider> }
    >(CREATE_CAREPROVIDER, {
      onCompleted,
      update: updateCompanyCareproviders,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateCompany({
  onCompleted,
}: MutationHookOptions<{ company: Company }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ company: Company }, { input: Partial<Company> }>(
      CREATE_COMPANY,
      {
        onCompleted,
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useGetAccountWithinCareseekerGroup({
  careseekerId,
  ...options
}: Pick<
  MutationHookOptions<{ account: Account }>,
  "onCompleted" | "onError"
> & {
  careseekerId: number;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { account: Account },
      { careseekerId: number; input: { email: string } }
    >(GET_ACCOUNT_WITHIN_CARESEEKER_GROUP, {
      variables: { careseekerId },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAccountForCareseekerMutation({
  careseekerId,
  ...options
}: Pick<
  MutationHookOptions<
    { account: Account },
    { careseekerId?: number; timestamp?: string }
  >,
  "onCompleted" | "update"
> & { careseekerId?: number }) {
  const translations = useTranslations();
  const toast = useToast();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { account: Account },
      { careseekerId?: number; input: Partial<Account>; timestamp?: string }
    >(CREATE_ACCOUNT_FOR_CARESEEKER, {
      onError: (err) => {
        toast({
          color: "danger",
          message: getCreateAccountErrorMessage({ error: err, translations }),
        });
      },
      refetchQueries: ["getAccountsForCareseeker"],
      variables: { careseekerId },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateAccountForCareseekerMutation({
  accountId,
  careseekerId,
  ...options
}: Omit<
  MutationHookOptions<
    { account: Account },
    { accountId?: number; careseekerId?: number; timestamp?: string }
  >,
  "refetchQueries"
> & {
  accountId?: number;
  careseekerId?: number;
  timestamp?: string;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { account: Account },
      {
        accountId?: number;
        careseekerId?: number;
        input: Partial<Account>;
        timestamp?: string;
      }
    >(UPDATE_ACCOUNT_FOR_CARESEEKER, {
      refetchQueries: ["getAccountsForCareseeker"],
      variables: { accountId, careseekerId },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAccount({
  ...options
}: MutationHookOptions<{ account: Account }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ account: Account }, { input: Partial<Account> }>(
      CREATE_ACCOUNT,
      { ...options },
    );

  // TODO make sure data.receiverNote is get correctly on use
  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateAccountRoleStationMapping(
  options?: MutationHookOptions<{
    accountRoleStation: AccountRoleStationInput;
  }>,
) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { accountRoleStation: AccountRoleStationInput },
      { input: AccountRoleStationInput }
    >(UPDATE_ACCOUNT_ROLE_STATION_MAPPING, { ...options });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateCareseeker({
  id,
  onCompleted,
  onError,
}: {
  id: number;
} & MutationHookOptions<{ careseeker: Careseeker }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careseeker: Careseeker },
      { id: number; input: Partial<Careseeker> }
    >(UPDATE_CARESEEKER, {
      onCompleted,
      onError,
      variables: { id },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateCareprovider({
  id,
  token,
  ...options
}: {
  id: number;
  token?: string;
} & MutationHookOptions<
  { careprovider: Careprovider },
  { id: number; token?: string }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careprovider: Careprovider },
      { id: number; input: Partial<Careprovider>; token?: string }
    >(token ? UPDATE_CAREPROVIDER_TOKEN : UPDATE_CAREPROVIDER, {
      variables: { id, token },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useArchivePatientMutation({
  id,
  onCompleted,
  onError,
}: {
  id: number;
} & MutationHookOptions) {
  return useGenericMutation<
    { patient: Patient },
    { id: number; input: { archived: boolean; id: number } }
  >(ARCHIVE_PATIENT, {
    onCompleted,
    onError,
    variables: { id },
  });
}

export function useOauthAuthentication({
  careproviderId,
  integrationId,
  onCompleted,
  onError,
}: {
  careproviderId: number;
  integrationId: number;
} & MutationHookOptions<{ onpremiseConfig: "success" }>) {
  return useGenericMutation<
    { onpremiseConfig: "success" },
    {
      careproviderId: number;
      input: OauthAuthenticationInput;
      integrationId: number;
    }
  >(CONNEXT_AUTHENTICATION, {
    onCompleted,
    onError,
    refetchQueries: [
      { query: GET_CAREPROVIDER({}), variables: { id: careproviderId } },
    ],
    variables: { careproviderId, integrationId },
  });
}

export function useIncrementOnpremiseDtaFileNumber({
  auction,
}: {
  auction: Auction;
}) {
  const careseekerId = auction.patient?.careseeker?.id;
  const insuranceIkNumber =
    auction.patient?.profile?.financing?.insurance?.ik_number;
  const hospitalIkNumber =
    auction.patient?.careseeker?.company?.ik_numbers?.[0];

  if (!careseekerId || !insuranceIkNumber || !hospitalIkNumber) {
    console.error(
      "[useIncrementOnpremiseDtaFileNumber] careseekerId, insuranceIkNumber, and hospitalIkNumber are all required",
      {
        auctionId: auction.id,
        careseekerId,
        hospitalIkNumber,
        insuranceIkNumber,
      },
    );
  }

  return useGenericMutation<
    { onpremiseDtaFileNumber: { file_number: number } },
    {
      careseekerId: number;
      hospitalIkNumber: number;
      insuranceIkNumber: number;
    }
  >(ONPREMISE_DTA_FILE_NUMBER, {
    variables: {
      careseekerId: careseekerId ?? -1,
      hospitalIkNumber: hospitalIkNumber ?? -1,
      insuranceIkNumber: insuranceIkNumber ?? -1,
    },
  });
}

export function useUpsertOnpremiseConfig({
  configId,
  onCompleted,
  onError,
}: {
  configId: number;
} & MutationHookOptions<{ onpremiseConfig: OnpremiseConfig }>) {
  const isNew = configId === -1;
  const UPSERT_ONPREMISE_CONFIG = isNew
    ? CREATE_ONPREMISE_CONFIG
    : UPDATE_ONPREMISE_CONFIG;

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { onpremiseConfig: OnpremiseConfig },
      { configId: number; input: OnpremiseConfigInput }
    >(UPSERT_ONPREMISE_CONFIG, {
      onCompleted,
      onError,
      variables: isNew ? undefined : { configId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateOnPremImport({
  careseekerId,
  onCompleted,
  onError,
}: {
  careseekerId: number;
} & MutationHookOptions<{ onpremiseConfig: OnPremImport }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { onpremiseConfig: OnPremImport },
      { careseekerId: number; input: Partial<OnPremImport> }
    >(CREATE_ONPREMISE_IMPORT, {
      onCompleted,
      onError,
      variables: { careseekerId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useGetAddressMutation(
  options?: MutationHookOptions<{ address: AddressCircle }>,
) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { address: AddressCircle },
      { input: Partial<AddressCircle> }
    >(GET_ADDRESS, {
      onCompleted: options?.onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateFeedbackPatientInfoMissing({
  auctionRequestId,
  careproviderId,
  onCompleted,
  refetchPatientEvents,
}: {
  auctionRequestId: number | null | undefined;
  careproviderId: number | null | undefined;
  refetchPatientEvents?: boolean;
} & MutationHookOptions<
  { feedbackPatientInfoMising: unknown },
  {
    auctionRequestId: number | null | undefined;
    careproviderId: number | null | undefined;
    input: string;
  }
>) {
  const refetchQueries = [];

  if (auctionRequestId && refetchPatientEvents) {
    refetchQueries.push(
      getAuctionRequestEventQuery({
        auctionRequestId,
      }),
    );
  }
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { feedbackPatientInfoMising: unknown },
      {
        auctionRequestId: number | null | undefined;
        careproviderId: number | null | undefined;
        input: string;
      }
    >(FEEDBACK_PATIENT_INFO_MISSING, {
      onCompleted,
      refetchQueries: refetchQueries.length ? refetchQueries : [],
      variables: { auctionRequestId, careproviderId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateSenderReceiverNote({
  careproviderId,
  careseekerId,
  onCompleted,
}: {
  careproviderId: number | null | undefined;
  careseekerId: number | null | undefined;
} & MutationHookOptions<{ senderReceiverNote: SenderReceiverNote }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { senderReceiverNote: SenderReceiverNote },
      {
        careproviderId: number | null | undefined;
        careseekerId: number | null | undefined;
        input: Partial<SenderReceiverNote>;
      }
    >(UPDATE_SENDER_RECEIVER_NOTE, {
      onCompleted,
      update: (proxy, { data }) => {
        proxy.writeQuery({
          data,
          query: GET_SENDER_RECEIVER_NOTE,
          variables: { careproviderId, careseekerId },
        });
      },
      variables: { careproviderId, careseekerId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateAssociations() {
  const [apolloMutation, { data, error, loading }] = useMutation<
    { association: null },
    { input: ProviderBlackListing }
  >(UPDATE_ASSOCIATIONS);
  const [mutation, mutationProgress, resetProgress] = useGenericProgress<
    { association: null },
    { input: ProviderBlackListing }
  >(apolloMutation, { error, loading });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUploadCareproviderDpa({
  careproviderId,
  fileType,
}: {
  careproviderId: number;
  fileType: number;
}) {
  const { setLegalDocs } = useLegalDocumentsContext();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { uploadDpaFile: DPAFileType },
      {
        careproviderId: number;
        fileType: ValueOf<typeof FILE_TYPES>;
        input: Partial<DPAFileType>;
      }
    >(UPLOAD_CAREPROVIDER_DPA, {
      onCompleted: () => {
        setLegalDocs({ careproviderId });
      },
      variables: {
        careproviderId,
        fileType,
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeleteCareproviderDpa({
  careproviderId,
}: {
  careproviderId: number;
}) {
  const { setLegalDocs } = useLegalDocumentsContext();
  const [mutation, { data, error, loading }] = useMutation<
    { deleteDpaFile: CareproviderDpaFile },
    { careproviderId: number }
  >(DELETE_CAREPROVIDER_DPA, {
    onCompleted: () => setLegalDocs({ careproviderId }),
    update: (cache) => {
      const query = gql`
          query Careprovider {
            careprovider {
              id
              ${careproviderDpa()}
            }
          }
        `;

      cache.writeQuery({
        data: {
          careprovider: {
            __typename: "Careprovider",
            data_processing_agreement: null,
            id: careproviderId,
          },
        },
        query,
      });
    },
    variables: { careproviderId },
  });

  const [deleteDpaFile, mutationProgress, resetProgress] = useGenericProgress<
    { deleteDpaFile: CareproviderDpaFile },
    { careproviderId: number }
  >(mutation, { error, loading });

  return [deleteDpaFile, mutationProgress, resetProgress, data, error] as const;
}

export function useUploadCareproviderFile({
  careproviderId,
  careproviderToken,
  fileType,
}: {
  careproviderId: number;
  careproviderToken?: string;
  fileType: number;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { uploadFile: CareproviderFile },
      {
        careproviderId: number;
        fileType: number;
        input: Partial<CareproviderFile>;
        params: string;
      }
    >(UPLOAD_CAREPROVIDER_FILE, {
      refetchQueries: [
        {
          query: GET_CAREPROVIDER_FILES,
          variables: {
            careproviderId,
            params: careproviderToken ? `?token=${careproviderToken}` : "",
          },
        },
      ],
      variables: {
        careproviderId,
        fileType,
        params: careproviderToken ? `?token=${careproviderToken}` : "",
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeleteCareproviderFile({
  careproviderId,
  careproviderToken,
}: {
  careproviderId: number;
  careproviderToken?: string;
}) {
  const [mutation, { error, loading }] = useMutation<
    { deleteFile: CareproviderFile },
    Partial<{
      careproviderId: number;
      fileId: number;
      params: string;
    }>
  >(DELETE_CAREPROVIDER_FILE, {
    refetchQueries: [
      {
        query: GET_CAREPROVIDER_FILES,
        variables: {
          careproviderId,
          params: careproviderToken ? `?token=${careproviderToken}` : "",
        },
      },
    ],
    variables: {
      careproviderId,
      params: careproviderToken ? `?token=${careproviderToken}` : "",
    },
  });
  const [deleteFile, mutationProgress, resetProgress] = useGenericProgress<
    { deleteFile: CareproviderFile },
    Partial<{
      careproviderId: number;
      fileId: number;
      params: string;
    }>
  >(mutation, { error, loading });

  return [deleteFile, mutationProgress, resetProgress, error] as const;
}

export function useCreateUserManagementFile() {
  const [mutation, mutationProgres, resetProgress, data, error] =
    useGenericMutation<
      { file: SenderUserManagementFile | undefined },
      { input: CreateUserManagmentFileParams }
    >(CREATE_USER_MANAGEMENT_FILE, {
      refetchQueries: ["getCareseeker", "getAccounts"],
    });

  return [mutation, mutationProgres, resetProgress, data?.file, error] as const;
}

export function useDeleteUserManagementFile({ id }: { id: number }) {
  const [mutation, mutationProgres, resetProgress, data, error] =
    useGenericMutation<
      { file: SenderUserManagementFile | undefined },
      { id: number }
    >(DELETE_USER_MANAGEMENT_FILE, {
      refetchQueries: ["getCareseeker", "getAccounts"],
      variables: { id },
    });

  return [mutation, mutationProgres, resetProgress, data?.file, error] as const;
}

export const useCreateFileMutation = ({ params }: { params: FileParams }) => {
  const [mutation, mutationProgres, resetProgress, data, error] =
    useGenericMutation<
      { file: Filev2 },
      {
        input: {
          careprovider_id?: number;
          careseeker_id?: number;
          category?: Filev2["category"];
          category_other?: string;
          file_type?: Filev2["file_type"];
          kis_document_id_string?: Filev2["kis_document_id_string"];
          file_scope?: FileScope;
          file_size?: Filev2["file_size"];
          recare_file_type?: RecareFileType;
        };
        params: FileParams;
      }
    >(CREATE_FILE, {
      update(cache, { data: mutationResult }) {
        const newFile = mutationResult?.file;
        if (!newFile) return;

        const queryToUpdate = {
          query: GET_FILES,
          variables: { params },
        };

        try {
          const data = cache.readQuery<{ files: Filev2[] }>(queryToUpdate);

          const newFilesList = [newFile, ...(data?.files || [])];

          cache.writeQuery<{ files: Filev2[] }>({
            ...queryToUpdate,
            data: {
              files: newFilesList,
            },
          });
        } catch (e) {
          console.error(e);
        }
      },
      variables: { params },
    });

  return [mutation, mutationProgres, resetProgress, data?.file, error] as const;
};

export function useUpdateFileMutation({
  id,
  params,
}: {
  id: number;
  params: FileParams;
}) {
  const { eventsQuery } = useAuctionRequestEventsContext();
  const [mutation, progress, reset, data, error] = useGenericMutation<
    { file: Filev2 | undefined },
    {
      id: Filev2["id"];
      input: {
        category?: FileCategory;
        file_content?: EncryptedField;
        file_name?: EncryptedField;
        kis_document_id_string?: Filev2["kis_document_id_string"];
        share_mode?: FileShareMode;
      };
      params: FileParams;
    }
  >(UPDATE_FILE, {
    refetchQueries: eventsQuery ? [eventsQuery] : undefined,
    update(cache, { data: mutationResult }) {
      const updatedFile = mutationResult?.file;
      if (!updatedFile) return;

      const queryToUpdate = {
        query: GET_FILES,
        variables: { params },
      };

      try {
        const data = cache.readQuery<{ files: Filev2[] }>(queryToUpdate);

        if (data) {
          const newFilesList = data.files.map((file) => {
            if (file.id === updatedFile.id) {
              return updatedFile;
            }
            return file;
          });

          cache.writeQuery<{ files: Filev2[] }>({
            ...queryToUpdate,
            data: {
              files: newFilesList,
            },
          });
        }
      } catch (e) {
        console.error(e);
      }
    },

    variables: { id, params },
  });

  return [mutation, progress, reset, data?.file, error] as const;
}

export function useDeleteFileMutation({
  id,
  onCompleted,
  onError,
  params,
}: { id: number; params: FileParams } & MutationHookOptions<{
  file: Filev2 | undefined;
}>) {
  const { eventsQuery } = useAuctionRequestEventsContext();
  const [mutation, progress, reset, data, error] = useGenericMutation<
    { file: Filev2 | undefined },
    { id: number; params: FileParams }
  >(DELETE_FILE, {
    onCompleted,
    onError,
    refetchQueries: [...(eventsQuery ? [eventsQuery] : [])],
    update(cache, _, { variables: mutationVariables }) {
      const deletedFileId = mutationVariables?.id;
      if (!deletedFileId) return;

      const queryToUpdate = {
        query: GET_FILES,
        variables: { params },
      };

      try {
        const data = cache.readQuery<{ files: Filev2[] }>(queryToUpdate);

        if (data) {
          const newFilesList = data.files.filter(
            (file) => file.id !== deletedFileId,
          );

          cache.writeQuery<{ files: Filev2[] }>({
            ...queryToUpdate,
            data: {
              files: newFilesList,
            },
          });
        }
      } catch (e) {
        console.error(e);
      }
    },

    variables: { id, params },
  });

  return [mutation, progress, reset, data?.file, error] as const;
}

export function useRequestActionMutation({
  actionType,
  onCompleted,
  onError,
  refetchQueries,
}: {
  actionType: RequestActionType | UndoActionType;
  refetchQueries?: BaseMutationOptions["refetchQueries"];
} & MutationHookOptions<{ auctionRequest: AuctionRequest }>) {
  const refetch = useRefetchOnConflict();
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });

  const memoisedMutation = useMemo(
    () => USE_REQUEST_ACTION(actionType),
    [actionType],
  );

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { auctionRequest: AuctionRequest },
      {
        id: number;
        input: {
          context: AnyObject;
          ref: string;
          request_id: number;
        };
      }
    >(memoisedMutation, {
      context: { encryptionContext },
      ...(refetchQueries ? { refetchQueries } : {}),
      onCompleted,
      onError,
    });

  const requestAction = useCallback(
    async (
      input: {
        context: AnyObject;
        ref: string;
        request_id: number;
      },
      variables: { id: number },
    ) => {
      try {
        return await mutation(input, variables);
      } catch (err) {
        if (input?.request_id && input.request_id > 0) {
          refetch({
            context: { encryptionContext },
            error: err,
            query: GET_AUCTIONREQUEST,
            variables: { id: input.request_id, ref: getReferrer() },
          });
        } else {
          console.error(
            `[DEV-14068] [useRefetchOnConflict] tried to access bad request id ${input.request_id}`,
          );
        }
        return null;
      }
    },
    [mutation, refetch, encryptionContext],
  );

  return [requestAction, mutationProgress, resetProgress, data, error] as const;
}

export function useSearchActionMutation({
  actionType,
  refetchQueries,
}: {
  actionType: SearchActionsWithHandlers;
  refetchQueries?: BaseMutationOptions["refetchQueries"];
}) {
  const encryptionContext = useApolloEncryptionContext();

  const memoisedMutation = useMemo(
    () => USE_SEARCH_ACTION(actionType),
    [actionType],
  );

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { auction: Auction },
      {
        auctionId: number;
        input: {
          auction_id: number;
          context: AnyObject;
        };
      }
    >(memoisedMutation, {
      context: { encryptionContext },
      ...(refetchQueries ? { refetchQueries } : {}),
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useAcceptTermsAndConditions({
  careprovider,
}: {
  careprovider: Careprovider | undefined;
}) {
  const { setLegalDocs } = useLegalDocumentsContext();
  const { getDocument } = useLegalDocuments({
    careproviderId: careprovider?.id,
  });
  const tcVersion = getDocument(DOCUMENT_TERMS_AND_CONDITIONS)?.version;

  const [update, progress] = useUpdateCareprovider({
    id: careprovider?.id ?? -1,
    onCompleted: () => {
      setLegalDocs({ careproviderId: careprovider?.id });
    },
  });

  const updateTC = () => {
    if (!careprovider) {
      console.error("careprovider undefined in useAcceptTermsAndConditions");
      return null;
    }

    if (!tcVersion) {
      console.error(
        "document version undefined in useAcceptTermsAndConditions",
      );
    }

    return update({
      terms_and_conditions: {
        date: getUnixTime(new Date()),
        version: tcVersion,
      },
    });
  };

  return [updateTC, progress] as const;
}

export function useAcceptDPA({
  careproviderId,
  isChurchOwned,
}: {
  careproviderId: number;
  isChurchOwned: boolean;
}) {
  const { getDocument } = useLegalDocuments({
    careproviderId,
  });
  const dpaVersion = getDocument(DOCUMENT_DATA_PROCESSING_AGREEMENT)?.version;

  const [uploadDpaFile, mutationProgress] = useUploadCareproviderDpa({
    careproviderId,
    fileType: FILE_TYPES.DOCUMENTS,
  });
  const updateDPA = useCallback(
    (input?: Partial<DPAFileType>) => {
      if (!dpaVersion) {
        console.error("document version undefined in useAcceptDPA");
        return null;
      }

      return uploadDpaFile({
        ...input,
        church_owned: isChurchOwned,
        version: dpaVersion,
      });
    },
    [uploadDpaFile, dpaVersion],
  );
  return [updateDPA, mutationProgress] as const;
}

export function usePostEncryptionJob() {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { encryptionJob: null },
      { auctionId: number; id: number; input: EncryptionJobRequest }
    >(POST_ENCRYPTION_JOB);

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateEncryptionContext() {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { encryptionContext: EncryptionContext },
      {
        input: {
          [x: string]: string | number | undefined;
          seald_display_id?: string;
          seald_id: string;
          seald_type: number;
        };
      }
    >(CREATE_ENCRYPTION_CONTEXT);

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useInitiateSealdIdentity() {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { sealdIdentity: SealdIdentityPayload },
      { input: Partial<SealdIdentityInput> }
    >(INITIATE_SEALD_IDENTITY, {});

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function usePostReceiverDocsApiFile() {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { receiverDocsAPIFile: null },
      {
        integrationId: number;
        input: { file_id: number; encrypted_content: string };
      }
    >(POST_RECEIVER_DOCS_API_FILE, {});

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateBrowserSubscription({
  id,
  onCompleted,
}: {
  id?: number;
} & MutationHookOptions<
  { browserSubscription: BrowserSubscription },
  {
    id?: number;
    input: {
      endpoint?: string;
      expirationTime?: number | null | undefined;
      id_token: string | null;
      keys?: Record<string, string> | undefined;
    };
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { browserSubscription: BrowserSubscription },
      {
        id?: number;
        input: {
          endpoint?: string;
          expirationTime?: number | null | undefined;
          id_token: string | null;
          keys?: Record<string, string> | undefined;
        };
      }
    >(UPDATE_BROWSER_SUBSCRIPTION, {
      onCompleted,
      variables: { id },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useStartElectiveFlow() {
  return useGenericMutation<{ electiveFlow: null }>(START_ELECTIVE_FLOW);
}

export function useDeleteSealdAccess() {
  return useGenericMutation<
    { sealdIdentity: null },
    { input: SealdAccessPayload }
  >(DELETE_SEALD_ACCESS, {});
}

export function useCreateReverseSearch({
  careproviderId,
  onCompleted,
  onError,
}: {
  careproviderId: number;
} & MutationHookOptions<{ reverseSearch: ReverseSearch }>) {
  return useGenericMutation<
    { reverseSearch: ReverseSearch },
    {
      careproviderId: number;
      input: { careprovider_id: number; filter: ReceiverFilters };
    }
  >(CREATE_CAREPROVIDER_REVERSE_SEARCH, {
    onCompleted,
    onError,
    refetchQueries: [
      {
        query: GET_CAREPROVIDER_REVERSE_SEARCHES,
        variables: { careproviderId },
      },
    ],
    variables: { careproviderId },
  });
}

export function useDeleteReverseSearch({
  careproviderId,
  onCompleted,
  onError,
  searchId,
}: {
  careproviderId: number | undefined;
  searchId: number | undefined;
} & MutationHookOptions<{ deleteReverseSearch: ReverseSearch }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { deleteReverseSearch: ReverseSearch },
      { careproviderId: number | undefined; searchId: number | undefined }
    >(DELETE_CAREPROVIDER_REVERSE_SEARCH, {
      onCompleted,
      onError,
      refetchQueries: [
        {
          query: GET_CAREPROVIDER_REVERSE_SEARCHES,
          variables: { careproviderId },
        },
      ],
      variables: { careproviderId, searchId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateReverseSearch({
  careproviderId,
  onCompleted,
  onError,
  searchId,
}: {
  careproviderId: number | undefined;
  searchId?: number;
} & MutationHookOptions<{ updateReverseSearch: ReverseSearch }>) {
  return useGenericMutation<
    { updateReverseSearch: ReverseSearch },
    {
      careproviderId: number | undefined;
      input: Partial<ReverseSearch>;
      searchId?: number;
    }
  >(UPDATE_CAREPROVIDER_REVERSE_SEARCH, {
    onCompleted,
    onError,
    refetchQueries: [
      {
        query: GET_CAREPROVIDER_REVERSE_SEARCHES,
        variables: { careproviderId },
      },
    ],
    variables: { careproviderId, searchId },
  });
}

export function useUpdateSearchCandidates() {
  return useGenericMutation<
    { searchCandidates: Candidates },
    { input: Partial<Auction> }
  >(UPDATE_SEARCH_CANDIDATES);
}

export function useCompleteProductTour(tourId: number | undefined) {
  return useGenericMutation<
    { productTour: ProductTourCompletion },
    {
      input: { completion_rate: number; step_id: number };
      tourId: number | undefined;
    }
  >(COMPLETE_PRODUCT_TOUR, { variables: { tourId } });
}

export function useCheckSealdSessions() {
  return useGenericMutation<
    { sealdSessions: SealdSessionsPayload },
    {
      auction_request_id?: number;
      file_id?: number;
      input: { [x: string]: number };
      patient_id?: number;
    }
  >(CHECK_SEALD_SESSIONS, {});
}

const useGetProviderSearchRedirectOnLogin = () => {
  const [search] = useSearchParams();
  const location = useLocation();

  search.delete("challenge");
  search.delete(REDIRECT_PARAM);

  // param used for if a user accessed an authenticated route whilst logged out
  // this is passed to the BE so they can create a log in link with tokens for the user
  // to access the page they intended from their email
  const zipcodeRedirect = search.get(RETURN_PARAM);
  if (zipcodeRedirect) {
    return zipcodeRedirect;
  }

  const searchString = search.toString();

  return encodeURIComponent(
    `${location.pathname}${searchString?.length ? `?${searchString}` : ""}`,
  );
};

export function useCreateProviderSearchAccount({
  onCompleted,
  onError,
}: MutationHookOptions<{ account: null }>) {
  const redirectTo = useGetProviderSearchRedirectOnLogin();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { account: null },
      { input: { email: string }; redirectTo: string }
    >(CREATE_PROVIDER_SEARCH_ACCOUNT, {
      onCompleted,
      onError,
      variables: { redirectTo },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useLoginProviderSearch({
  onCompleted,
  onError,
}: MutationHookOptions<{ account: null }>) {
  const redirectTo = useGetProviderSearchRedirectOnLogin();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { account: null },
      { input: { email: string }; redirectTo: string }
    >(LOGIN_PROVIDER_SEARCH_ACCOUNT, {
      onCompleted,
      onError,
      variables: { redirectTo },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreatePatientMutation() {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ patient: Patient }, { input: Partial<Patient> }>(
      CREATE_PATIENT,
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateProviderSearchCareseeker() {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careseeker: Careseeker },
      { input: Partial<Careseeker> }
    >(CREATE_PROVIDERSEARCH_CARESEEKER, {
      update: (proxy, { data }) => {
        const careseeker = data?.careseeker;
        const variables = { id: careseeker?.id };
        proxy.writeQuery({
          data: {
            careseeker,
          },
          // should match query signiture in useLoggedCareseeker
          query: GET_CARESEEKER({ withAdminFields: true, withConfig: false }),
          variables,
        });
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useArchiveProviderSearchPatient({
  id,
  onCompleted,
}: {
  id: number;
} & MutationHookOptions<{ patient: Patient }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ patient: Patient }, { id: number }>(
      ARCHIVE_PROVIDER_SEARCH_PATIENT,
      {
        onCompleted,
        refetchQueries: [
          "getProviderSearchRequestOverview",
          "getProviderSearchPatients",
        ],
        variables: { id },
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}
export function useSealdGroupReset({
  onCompleted,
  onError,
}: MutationHookOptions<
  { sealdGroup: null },
  { input: { seald_id: string | undefined } }
>) {
  return useGenericMutation<
    { sealdGroup: null },
    { input: { seald_id: string | undefined } }
  >(SEALD_GROUP_RESET, {
    onCompleted,
    onError,
    refetchQueries: ["getAccounts", "getCareprovider"],
  });
}

export function useUpdateConsultant({ id }: { id: number }) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { consultant: Consultant },
      { id: number; input: Partial<Consultant> }
    >(UPDATE_CONSULTANT, {
      refetchQueries: ["getConsultantEventPage"],
      variables: { id },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeleteConsultantFile({
  consultantId,
}: {
  consultantId: number;
}) {
  const [mutation, { error, loading }] = useMutation<
    { deleteFile: CareproviderFile },
    { consultantId: number; fileId?: number }
  >(DELETE_CONSULTANT_FILE, {
    refetchQueries: [
      "getConsultantEventPage",
      {
        query: GET_CONSULTANT_FILES,
        variables: {
          consultantId,
        },
      },
    ],
    variables: {
      consultantId,
    },
  });

  const [deleteFile, mutationProgress, resetProgress] = useGenericProgress<
    { deleteFile: CareproviderFile },
    { consultantId: number; fileId?: number }
  >(mutation, { error, loading });

  return [deleteFile, mutationProgress, resetProgress, error] as const;
}

export function useUploadConsultantFile({
  consultantId,
}: {
  consultantId: number;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      CareproviderFile,
      {
        consultantId: number;
        fileType: number;
        input: { content: string; file_name: string; file_type: number };
      }
    >(UPLOAD_CONSULTANT_FILE, {
      refetchQueries: [
        "getConsultantEventPage",
        {
          query: GET_CONSULTANT_FILES,
          variables: {
            consultantId,
          },
        },
      ],
      variables: {
        consultantId,
        fileType: FILE_TYPES.IMAGES,
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateConsultant({
  onCompleted,
}: MutationHookOptions<
  { consultant: Consultant },
  { input: Partial<Consultant> }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { consultant: Consultant },
      { input: Partial<Consultant> }
    >(CREATE_CONSULTANT, {
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateDoctorRehabForms({
  auctionId,
  patientId,
  ...options
}: {
  auctionId: number | undefined;
  patientId: number | undefined;
} & MutationHookOptions<
  UpdateDoctorRehabFormsData,
  UpdateDoctorRehabFormsVariables
>) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });

  const careseekerId = useLoggedCareseeker()?.id;
  if (!careseekerId) {
    throw new Error("careseeker id is required");
  }

  const [mutation, progress, reset, data, error] = useGenericMutation<
    UpdateDoctorRehabFormsData,
    UpdateDoctorRehabFormsVariables & {
      input: Partial<Auction>;
    }
  >(UPDATE_DOCTOR_REHAB_FORMS, {
    ...options,
    context: { encryptionContext },
    refetchQueries: [
      {
        context: { encryptionContext },
        query: GET_DOCTOR_REHAB_FORMS,
        variables: { auctionId, careseekerId, patientId },
      },
    ],
    variables: { auctionId, careseekerId, patientId },
  });

  return [progress, mutation, reset, data, error] as const;
}

export function useCreateChat(
  options?: MutationHookOptions<CreateChatData, CreateChatVariables>,
) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });

  const [mutation, progress, reset, data, error] = useGenericMutation<
    CreateChatData,
    CreateChatVariables & {
      input: Partial<Chat>;
    }
  >(CREATE_CHAT, {
    ...options,
    context: { encryptionContext },
  });

  return [progress, mutation, reset, data, error] as const;
}

export function useUpdateChat(
  options?: MutationHookOptions<
    { chat: Chat },
    { id?: number; input?: Partial<Chat> }
  >,
) {
  const encryptionContext = useApolloEncryptionContext({
    decrypt: true,
  });
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ chat: Chat }, { id?: number; input: Partial<Chat> }>(
      UPDATE_CHAT,
      {
        context: { encryptionContext },
        ...options,
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useDeleteChatMutation(
  options?: MutationHookOptions<DeleteChatData, DeleteChatVariables>,
) {
  const [mutation, progress, reset, data, error] = useGenericMutation<
    DeleteChatData,
    DeleteChatVariables
  >(DELETE_CHAT, {
    ...options,
  });

  return [progress, mutation, reset, data, error] as const;
}

export function useUpdateEmailTranslation(
  options?: MutationHookOptions<
    { translation: EmailTranslation },
    {
      id: string;
      input: {
        id: string;
        translation: FormValue;
      };
    }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { translation: EmailTranslation },
    {
      id: string;
      input: {
        id: string;
        translation: FormValue;
      };
    }
  >(UPDATE_EMAIL_TRANSLATION, options);
  const [updateTranslation, progress, reset] = useGenericProgress<
    { translation: EmailTranslation },
    {
      id: string;
      input: {
        id: string;
        translation: FormValue;
      };
    }
  >(mutation, { error, loading });

  const translation = data?.translation;
  return [updateTranslation, progress, reset, translation, error] as const;
}

export function useCreateEmailTranslation(
  options?: MutationHookOptions<
    { translation: EmailTranslation },
    { input: EmailTranslation }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { translation: EmailTranslation },
    { input: EmailTranslation }
  >(CREATE_EMAIL_TRANSLATION, options);

  const [updateTranslation, mutationProgress, resetProgress] =
    useGenericProgress<
      { translation: EmailTranslation },
      { input: EmailTranslation }
    >(mutation, {
      error,
      loading,
    });

  const mutate = useCallback(
    (input: EmailTranslation) => updateTranslation({ variables: { input } }),
    [updateTranslation],
  );

  const translation = data?.translation;
  return [mutate, mutationProgress, resetProgress, translation, error] as const;
}

export function useDeleteEmailTranslation(
  options?: MutationHookOptions<
    { translation: TranslationType },
    { id: string }
  >,
) {
  const [mutation, { data, error, loading }] = useMutation<
    { translation: TranslationType },
    { id: string }
  >(DELETE_EMAIL_TRANSLATION, options);
  const [mutate, progress, reset] = useGenericProgress<
    { translation: TranslationType },
    { id: string }
  >(mutation, { error, loading });

  const translation = data?.translation;
  return [mutate, progress, reset, translation, error] as const;
}

export function useSendTestEmail(
  options?: MutationHookOptions<
    { testEmail: SendEmail },
    {
      input: {
        country: Country;
        mail_type: number;
      };
    }
  >,
) {
  const [mutation, { error, loading }] = useMutation<
    { testEmail: SendEmail },
    {
      input: {
        country: Country;
        mail_type: number;
      };
    }
  >(SEND_TEST_EMAIL, options);
  const [sendEmail, progress, resetProgress] = useGenericProgress<
    {
      testEmail: SendEmail;
    },
    {
      input: {
        country: Country;
        mail_type: number;
      };
    }
  >(mutation, { error, loading });

  return [sendEmail, progress, resetProgress] as const;
}

export function useUpdateCapacity({
  careprovider,
  ...options
}: {
  careprovider: Careprovider;
} & MutationHookOptions<{ capacity: Capacity }>) {
  const [mutation, { error, loading }] = useMutation<
    { capacity: Capacity },
    Partial<{ id: number; input: Partial<Capacity> }>
  >(UPDATE_CAPACITY, {
    ...options,
    update(cache, { data }) {
      if (data?.capacity) {
        cache.writeQuery({
          data: {
            careprovider: {
              ...careprovider,
              __typename: "Careprovider",
              capacity: data.capacity,
            },
          },
          query: GET_CAREPROVIDER({}),
        });
      }
    },
  });
  const [mutate, mutationProgress] = useGenericProgress<
    { capacity: Capacity },
    Partial<{ id: number; input: Partial<Capacity> }>
  >(mutation, { error, loading });

  const confirmCapacity = () =>
    mutate({
      variables: {
        id: careprovider.id,
        input: {
          capacity_confirmed: true,
        },
      },
    });

  const updateCapacity = (capacity: Omit<Capacity, "capacity_confirmed">) =>
    mutate({
      variables: {
        id: careprovider.id,
        input: capacity,
      },
    });

  return { confirmCapacity, mutationProgress, updateCapacity };
}

export function useSaveBedrockPrompt() {
  const [mutation, progress, reset, data, error] = useGenericMutation<
    { prompt: BedrockPrompt | undefined },
    {
      input: Omit<BedrockPrompt, "id">;
    }
  >(SAVE_BEDROCK_PROMPT, {
    refetchQueries: ["getBedrockPlayground"],
  });

  return [mutation, progress, reset, data?.prompt, error] as const;
}

export function useSaveBedrockSchema() {
  const [mutation, progress, reset, data, error] = useGenericMutation<
    { schema: BedrockSchema | undefined },
    {
      input: Omit<BedrockSchema, "id">;
    }
  >(SAVE_BEDROCK_SCHEMAS, {
    refetchQueries: ["getBedrockPlayground"],
  });

  return [mutation, progress, reset, data?.schema, error] as const;
}

export function useSaveBedrockExample() {
  const [mutation, progress, reset, data, error] = useGenericMutation<
    { example: BedrockExample | undefined },
    {
      input: Omit<BedrockExample, "id">;
    }
  >(SAVE_BEDROCK_EXAMPLE, {
    refetchQueries: ["getBedrockPlayground"],
  });

  return [mutation, progress, reset, data?.example, error] as const;
}

export function useSaveBedrockResponse() {
  const [mutation, progress, reset, data, error] = useGenericMutation<
    { response: BedrockResponse | undefined },
    {
      input: BedrockResponsePayload;
    }
  >(SAVE_BEDROCK_RESPONSE, {
    refetchQueries: ["getBedrockPlayground"],
  });

  return [mutation, progress, reset, data?.response, error] as const;
}

export function useDeleteBedrockPrompt(id: number, onCompleted: () => void) {
  const [mutation, progress, reset, _data, error] = useGenericMutation<null>(
    DELETE_BEDROCK_PROMPT,
    {
      onCompleted,
      refetchQueries: ["getBedrockPlayground"],
      variables: { id },
    },
  );

  return [mutation, progress, reset, error] as const;
}

export function useDeleteBedrockSchema(id: number, onCompleted: () => void) {
  const [mutation, progress, reset, _data, error] = useGenericMutation<null>(
    DELETE_BEDROCK_SCHEMA,
    {
      onCompleted,
      refetchQueries: ["getBedrockPlayground"],
      variables: { id },
    },
  );

  return [mutation, progress, reset, error] as const;
}

export function useDeleteBedrockExample(id: number, onCompleted: () => void) {
  const [mutation, progress, reset, _data, error] = useGenericMutation<null>(
    DELETE_BEDROCK_EXAMPLE,
    {
      onCompleted,
      refetchQueries: ["getBedrockPlayground"],
      variables: { id },
    },
  );

  return [mutation, progress, reset, error] as const;
}

export function useDeleteBedrockResponse(id: number) {
  const [mutation, progress, reset, _data, error] = useGenericMutation<null>(
    DELETE_BEDROCK_RESPONSE,
    {
      refetchQueries: ["getBedrockPlayground"],
      variables: { id },
    },
  );

  return [mutation, progress, reset, error] as const;
}

export function useCareproviderAccountAssociation({
  id,
  onCompleted,
  onError,
  token,
}: MutationHookOptions<
  { accountAssociation: Careprovider },
  {
    id: number;
    input: { email: string };
    token?: string;
  }
> & {
  id: number;
  token?: string;
}) {
  return useGenericMutation<
    { accountAssociation: Careprovider },
    { input: { email: string } }
  >(CAREPROVIDER_ACCOUNT_ASSOCIATION, {
    onCompleted,
    onError,
    variables: { id, token },
  });
}

export function useForgotPassword({
  onCompleted,
  onError,
}: MutationHookOptions<
  undefined,
  {
    input: {
      app: AppType;
      email: string;
      onboarding_token: string | undefined;
    };
  }
>) {
  return useGenericMutation<
    undefined,
    {
      input: {
        app: AppType;
        email: string;
        onboarding_token: string | undefined;
      };
    }
  >(FORGOT_PASSWORD, {
    onCompleted,
    onError,
    variables: {
      endpoint: "reset/initiate",
    },
  });
}

export function useUpdateIkNumbers({
  id,
  onCompleted,
  onError,
  token,
}: {
  id: Careprovider["id"];
  token?: string;
} & MutationHookOptions<
  { company: Company },
  {
    id: Careprovider["id"];
    input: Pick<Company, "ik_numbers" | "id">;
    token?: string;
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { company: Company },
      {
        id: Careprovider["id"];
        input: Pick<Company, "ik_numbers" | "id">;
        token?: string;
      }
    >(UPDATE_IK_NUMBERS, {
      onCompleted,
      onError,
      variables: { id, token },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAdditionalAuction(
  options?: MutationHookOptions<
    { auction: Auction },
    {
      input: {
        search_type: number;
      };
      patientId: number;
    }
  >,
) {
  const [mutation, { error, loading }] = useMutation<
    { auction: Auction },
    {
      input: {
        search_type: number;
      };
      patientId: number;
    }
  >(CREATE_ADDITIONAL_AUCTION, options);

  const [createAdditionalAuction, progress, resetProgress] = useGenericProgress<
    { auction: Auction },
    {
      input: {
        search_type: number;
      };
      patientId: number;
    }
  >(mutation, { error, loading });

  return [createAdditionalAuction, progress, resetProgress] as const;
}

export function useSendOnpremiseLogs({
  configId,
  ...rest
}: MutationHookOptions<undefined, { configId: number | undefined }> & {
  configId: number | undefined;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<undefined, { configId: number | undefined }>(
      SEND_ONPREMISE_LOGS,
      { variables: { configId }, ...rest },
    );

  return [mutationProgress, mutation, resetProgress, data, error] as const;
}

export function useBatchAddProviderAccounts({
  careproviderId,
  ...options
}: {
  careproviderId: number;
} & MutationHookOptions<
  { batchAddProviderAccounts: Account[] },
  {
    id: number;
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { batchAddProviderAccounts: Account[] },
      {
        id: number;
      }
    >(BATCH_ADD_PROVIDER_ACCOUNTS, {
      variables: {
        id: careproviderId,
      },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAccountsBatch({
  ...options
}: {
  input?: Array<Partial<Account>>;
} & MutationHookOptions<{ accounts: Account[] }>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { accounts: Account[] },
      { input: Array<Partial<Account>> }
    >(CREATE_ACCOUNTS_BATCH, {
      refetchQueries: ["getAccounts"],
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useAddCareproviderMember({
  careproviderId,
  invite = true,
  token,
  ...options
}: {
  careproviderId: number;
  input?: Partial<Account>;
  invite?: boolean;
  token?: string;
} & MutationHookOptions<
  { careproviderMember: { accounts: Array<Account> } },
  {
    id: number;
    token?: string;
  }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { careproviderMember: { accounts: Array<Account> } },
      {
        id: number;
        input: Partial<Account>;
        token?: string;
      }
    >(
      token
        ? ADD_CAREPROVIDER_MEMBER_TOKEN({ invite })
        : ADD_CAREPROVIDER_MEMBER({ invite }),
      {
        variables: {
          id: careproviderId,
          token,
        },
        ...options,
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export const acpCareseekerQueryOptions = {
  withAdminFields: true,
  withConfig: true,
  withProductFeatures: true,
  withSSO: true,
};

export function useCreateOrUpdateCareseekerProductFeature({
  careseekerId,
}: {
  careseekerId: number;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { productFeature: ProductFeature },
      { careseekerId: number; input: ProductFeatureRequestPayload }
    >(CREATE_OR_UPDATE_CARESEEKER_PRODUCT_FEATURE, {
      refetchQueries: [
        {
          query: GET_CARESEEKER(acpCareseekerQueryOptions),
          variables: { id: careseekerId },
        },
      ],
      variables: {
        careseekerId,
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useLazyGenerateEmbedToken({
  careproviderId,
}: {
  careproviderId: number;
}) {
  const [generateEmbedToken] = useMutation<
    {
      generateEmbedToken: EmbedCookielessSessionAcquireResponse;
    },
    {
      id: number;
      input: {
        api_token: string;
        navigation_token: string;
      };
    }
  >(UPDATE_LOOKER_EMBED_TOKEN);

  return useCallback(
    async ({
      api_token,
      navigation_token,
    }: {
      api_token: string;
      navigation_token: string;
    }) => {
      const { data } = await generateEmbedToken({
        variables: {
          id: careproviderId,
          input: { api_token, navigation_token },
        },
      });

      return [data?.generateEmbedToken] as const;
    },
    [generateEmbedToken, careproviderId],
  );
}

export function useSendAdmissionsLoginEmail({
  admissionsId,
  options: { onCompleted, onError },
}: {
  admissionsId: string | undefined;
  options: MutationHookOptions<{ account: null }>;
}) {
  // TODO: store form meta data in the DB so we can get this step, managing the steps out of scope for this
  const LANDING_STEP = 0;
  const redirectTo = `/careprovider/admissions/${admissionsId}/step/${LANDING_STEP}`;

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ account: null }, { input: { email: string } }>(
      SEND_ADMISSIONS_LOGIN_EMAIL,
      {
        onCompleted,
        onError,
        variables: { admissionsId, redirectTo },
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpsertAdmissionForm({
  careproviderId,
}: { careproviderId: number } & MutationHookOptions<
  { admissionForm: AdmissionsForm },
  { careproviderId: number }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { admissionForm: AdmissionsForm },
      { careproviderId: number; input: Partial<AdmissionsForm> }
    >(UPSERT_ADMISSION_FORM, {
      refetchQueries: [
        { query: GET_CAREPROVIDER({}), variables: { id: careproviderId } },
      ],
      variables: { careproviderId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAdmissionsResources({
  admissionsId,
  careproviderId,
}: { careproviderId: number; admissionsId: string } & MutationHookOptions<
  { auction: Auction },
  { careproviderId: number; admissionsId: string }
>) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { auction: Auction },
      { careproviderId: number; admissionsId: string; input: { email: string } }
    >(CREATE_ADMISSIONS_RESOURCES, {
      variables: { admissionsId, careproviderId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateAdmissionsResources({
  careproviderId,
}: { careproviderId: number } & MutationHookOptions<
  { auction: Auction },
  { careproviderId: number }
>) {
  const encryptionContext = useApolloEncryptionContext({
    decrypt: true,
  });
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { auction: Auction },
      { careproviderId: number; input: DeepPartial<Auction> }
    >(UPDATE_ADMISSIONS_RESOURCES, {
      context: { encryptionContext },
      variables: { careproviderId },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateAdmissionsAuction() {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<void, { admissionsToken: string }>(
      UPDATE_ADMISSIONS_AUCTION,
    );

  const updateAdmissionsAuctionWithToken = useCallback(
    (admissionsToken: string) => {
      return mutation(undefined, { admissionsToken });
    },
    [mutation],
  );

  return [
    updateAdmissionsAuctionWithToken,
    mutationProgress,
    resetProgress,
    data,
    error,
  ] as const;
}

export function useCreateOrUpdateCareproviderProductFeature({
  careproviderId,
}: {
  careproviderId: number;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      { productFeature: ProductFeature },
      { careproviderId: number; input: ProductFeatureRequestPayload }
    >(CREATE_OR_UPDATE_CAREPROVIDER_PRODUCT_FEATURE, {
      refetchQueries: [
        {
          query: GET_CAREPROVIDER({}),
          variables: { id: careproviderId },
        },
      ],
      variables: {
        careproviderId,
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}
